import { Link } from "react-router-dom";
import { BananaType, BananaTypeProps } from "../types/BananaType";

const BananaCard: React.FC<{ banana: BananaTypeProps }> = ({ banana }) => (
  <Link
    to={`/tuttobanana?banana=${encodeURIComponent(banana.key)}`}
    aria-label={`Choose ${banana.title}`}
    className="group relative flex min-h-[10.5rem] flex-col items-center justify-between rounded-2xl border-2 border-gray-200 bg-white px-3 py-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-yellow-400 hover:shadow-md focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-200 sm:min-h-[12rem] sm:px-4 sm:py-5"
  >
    <img
      src={banana.img}
      alt=""
      className="h-24 w-24 flex-shrink-0 object-contain transition-transform group-hover:scale-105 sm:h-28 sm:w-28"
    />
    <div className="mt-3 flex min-h-[2.5rem] items-center justify-center">
      <h4 className="text-base font-bold leading-tight text-gray-900 sm:text-lg">{banana.title}</h4>
    </div>
    <span className="mt-2 text-sm font-semibold text-yellow-700 group-hover:text-yellow-800">Choose →</span>
  </Link>
);

interface CollectionSectionProps {
  title: string;
  items: BananaTypeProps[];
}

const CollectionSection: React.FC<CollectionSectionProps> = ({ title, items }) => (
  <section className="mb-10">
    <h3 className="mb-4 text-xl font-bold text-gray-900 sm:text-2xl">{title}</h3>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
      {items.map((item) => (
        <BananaCard key={item.key} banana={item} />
      ))}
    </div>
  </section>
);

const FeaturedSeasonalCard: React.FC<{ banana: BananaTypeProps }> = ({ banana }) => (
  <Link
    to={`/tuttobanana?banana=${encodeURIComponent(banana.key)}`}
    aria-label={`Choose featured banana ${banana.title}`}
    className="group mb-10 grid grid-cols-[7rem_1fr] items-center gap-4 overflow-hidden rounded-2xl border-2 border-yellow-400 bg-yellow-50 p-4 shadow-sm transition hover:border-yellow-500 hover:shadow-md focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-200 sm:grid-cols-[9rem_1fr_auto] sm:p-5"
  >
    <div className="relative">
      <span className="absolute -left-1 -top-2 rounded-full bg-gray-900 px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wide text-white">Seasonal</span>
      <img src={banana.img} alt="" className="h-28 w-28 object-contain sm:h-36 sm:w-36" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-yellow-700">Featured right now</p>
      <h3 className="mt-1 text-xl font-black leading-tight text-gray-900 sm:text-2xl">{banana.title}</h3>
      <p className="mt-2 hidden text-base leading-snug text-gray-600 sm:block">{banana.description}</p>
      <span className="mt-3 inline-block text-sm font-bold text-gray-900 sm:hidden">Choose →</span>
    </div>
    <span className="hidden rounded-full bg-gray-900 px-5 py-2.5 text-sm font-bold text-white transition group-hover:bg-yellow-500 group-hover:text-gray-900 sm:inline-flex">Choose →</span>
  </Link>
);

export function BananaCollections() {
  const featuredSeasonal = BananaType.getFeaturedSeasonal();
  const remainingSeasonal = featuredSeasonal
    ? BananaType.bananaCollectionSeasonal.filter((banana) => banana.key !== featuredSeasonal.key)
    : BananaType.bananaCollectionSeasonal;

  return (
    <>
      {featuredSeasonal && <FeaturedSeasonalCard banana={featuredSeasonal} />}
      <CollectionSection title="Everyday" items={BananaType.bananaCollectionEveryday} />
      <CollectionSection title="Celebrations" items={BananaType.bananaCollectionCelebration} />
      <CollectionSection title="Care & Comfort" items={BananaType.bananaCollectionCareAndComfort} />
      <CollectionSection title="Seasonal" items={remainingSeasonal} />
    </>
  );
}
